import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BarChart3, TrendingUp, Shield, Users, KeyRound, Eye, EyeOff, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthPage({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'user',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, loginData);
      if (response.data.mfa_required) {
        setMfaToken(response.data.mfa_token);
        setMfaStep(true);
        setTotpCode('');
        toast.info('Enter your authenticator code to continue');
      } else {
        toast.success('Login successful!');
        onLogin(response.data.user, response.data.access_token);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = useRecovery ? '/api/auth/mfa/verify-recovery' : '/api/auth/mfa/verify-login';
      const payload = useRecovery ? { code: totpCode } : { code: totpCode };
      const response = await axios.post(`${API_URL}${endpoint}`, payload, {
        headers: { Authorization: `Bearer ${mfaToken}` },
      });
      toast.success('Login successful!');
      onLogin(response.data.user, response.data.access_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, registerData);
      toast.success('Registration successful!');
      onLogin(response.data.user, response.data.access_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: BarChart3, title: 'Spend Analytics', desc: 'Comprehensive spend visibility' },
    { icon: TrendingUp, title: 'Cost Savings', desc: 'Identify optimization opportunities' },
    { icon: Shield, title: 'Risk Management', desc: 'Supplier risk assessment' },
    { icon: Users, title: 'Supplier Insights', desc: 'Deep vendor analysis' },
  ];

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left Side - Hero Section */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background image with dark overlay */}
        <img
          src="https://images.unsplash.com/photo-1769667693205-bfae7aefb59a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBpbnRlcmlvciUyMGdsYXNzJTIwd2FsbHMlMjBwbGFudHMlMjBtb2Rlcm58ZW58MHx8fHwxNzcyODIwNzc1fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#001f5c]/80"></div>

        <div className="relative z-10 flex flex-col p-10 pb-20 text-white h-full">
          {/* Top-left logo */}
          <motion.div
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.05 }}
            className="mb-2"
          >
            <h1 className="text-5xl font-extrabold tracking-tight leading-[1]" data-testid="app-name-hero">
              <span className="text-white">Spend</span><span className="text-[#00aaff]">Lens</span>
            </h1>
            <p className="text-xs font-medium tracking-[3px] uppercase text-slate-400 mt-3">AI-Powered Spend Analytics</p>
          </motion.div>

          {/* Centered hero content */}
          <div className="flex-1 flex flex-col justify-center gap-6 mt-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-4xl font-bold leading-[1.15] mb-5 tracking-tight">
              Streamline your
              <span className="text-[#00aaff]"> spend management</span>
            </h2>
            <p className="text-sm text-slate-300 max-w-md leading-relaxed">
              Unlock savings, detect leakages, and make data-driven procurement decisions with powerful analytics.
            </p>
          </motion.div>

          {/* Feature cards */}
          <div className="grid grid-cols-2 gap-4 max-w-md">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 hover:bg-white/15 transition-colors"
              >
                <feature.icon className="w-6 h-6 text-[#00aaff] mb-2" />
                <h3 className="font-semibold text-sm text-white mb-1">{feature.title}</h3>
                <p className="text-xs text-slate-300 leading-snug">{feature.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="flex gap-10 pt-4"
          >
            <div>
              <p className="text-[32px] font-bold text-[#00aaff] leading-none">15%</p>
              <p className="text-xs text-slate-400 mt-2">Average Savings</p>
            </div>
            <div>
              <p className="text-[32px] font-bold text-[#00aaff] leading-none">500+</p>
              <p className="text-xs text-slate-400 mt-2">Organizations</p>
            </div>
            <div>
              <p className="text-[32px] font-bold text-[#00aaff] leading-none">$2B+</p>
              <p className="text-xs text-slate-400 mt-2">Spend Analyzed</p>
            </div>
          </motion.div>
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white"
      >
        <div className="w-full max-w-[420px]">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-[#001f5c] leading-none tracking-tight">Welcome back</h2>
            <p className="text-sm text-slate-500 mt-1.5">Sign in to continue to <span className="font-semibold text-[#00aaff]">SpendLens</span></p>
          </div>
              {!mfaStep ? (
                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-xs font-medium tracking-[0.025em] uppercase text-slate-600">Email Address</Label>
                    <Input
                      id="login-email"
                      type="email"
                      data-testid="login-email-input"
                      placeholder="you@company.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      className="h-11 px-4 border-slate-200 focus:border-[#00aaff] focus:ring-[#00aaff]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-xs font-medium tracking-[0.025em] uppercase text-slate-600">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? "text" : "password"}
                        data-testid="login-password-input"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        className="h-11 px-4 border-slate-200 focus:border-[#00aaff] focus:ring-[#00aaff] pr-10"
                      />
                      <button
                        type="button"
                        data-testid="toggle-password-visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 bg-[#001f5c] hover:bg-[#001845] text-white text-sm font-medium group mt-1"
                    disabled={loading}
                    data-testid="login-submit-button"
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      {loading ? 'Signing in...' : 'Sign In'}
                      {!loading && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />}
                    </span>
                  </Button>
                  <div className="text-center pt-1">
                    <a
                      href="/forgot-password"
                      className="text-sm text-slate-500 hover:text-[#001f5c] transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot your password?
                    </a>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleMfaVerify} className="space-y-5" data-testid="mfa-form">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <KeyRound className="w-5 h-5 text-blue-600 shrink-0" />
                    <p className="text-sm text-blue-800">
                      {useRecovery ? 'Enter one of your recovery codes' : 'Enter the 6-digit code from your authenticator app'}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium tracking-[0.025em] uppercase text-slate-600">{useRecovery ? 'Recovery Code' : 'Authentication Code'}</Label>
                    <Input
                      type="text"
                      data-testid="mfa-code-input"
                      placeholder={useRecovery ? 'e.g. e1AwgOrHYhs' : '000000'}
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                      autoFocus
                      required
                      maxLength={useRecovery ? 20 : 6}
                      className="h-11 px-4 border-slate-200 focus:border-[#00aaff] focus:ring-[#00aaff] text-center text-lg md:text-lg tracking-widest font-mono"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 bg-[#001f5c] hover:bg-[#001845] text-white text-sm font-medium"
                    disabled={loading}
                    data-testid="mfa-verify-button"
                  >
                    {loading ? 'Verifying...' : 'Verify'}
                  </Button>
                  <div className="flex justify-between items-center">
                    <button type="button" className="text-xs text-slate-500 hover:text-slate-700 underline"
                      onClick={() => setUseRecovery(!useRecovery)} data-testid="toggle-recovery">
                      {useRecovery ? 'Use authenticator instead' : 'Use a recovery code'}
                    </button>
                    <button type="button" className="text-xs text-slate-500 hover:text-slate-700 underline"
                      onClick={() => { setMfaStep(false); setMfaToken(''); setTotpCode(''); }} data-testid="back-to-login">
                      Back to login
                    </button>
                  </div>
                </form>
              )}
        </div>
      </motion.div>
    </div>
  );
}
